import { useState, useEffect } from 'react'
import { ArrowRight, Mail, Phone, Building, CheckCircle2, Sparkles, Zap } from 'lucide-react'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { v4 as uuidv4 } from 'uuid'
import ReactConfetti from 'react-confetti'

const isDevelopment = import.meta.env.MODE === 'development'
const API_URL = import.meta.env.VITE_API_BASE_URL || '';
const HCAPTCHA_SITE_KEY = import.meta.env.VITE_HCAPTCHA_SITE_KEY || '';

const companyTypes = [
  { id: 'startup', name: 'Startup en etapa temprana' },
  { id: 'scaleup', name: 'Scaleup en crecimiento' },
  { id: 'enterprise', name: 'Empresa establecida' },
  { id: 'agency', name: 'Agencia o consultora' },
  { id: 'other', name: 'Otro' }
]

const subscriptionPlans = [
  { name: 'Basic', pricePerUser: 10 },
  { name: 'Pro', pricePerUser: 20 },
  { name: 'Enterprise', pricePerUser: 15 }
]

const userRanges = [
  { min: 1, max: 5, label: '1-5 usuarios' },
  { min: 6, max: 10, label: '6-10 usuarios' },
  { min: 11, max: 20, label: '11-20 usuarios' },
  { min: 21, max: 50, label: '21-50 usuarios' },
  { min: 51, max: null, label: 'Más de 50 usuarios' }
]

const benefits = [
  {
    title: 'Validación rápida',
    description: 'Comienza a validar tu idea en menos de 24 horas',
    icon: Zap
  },
  {
    title: 'Soporte personalizado',
    description: 'Te acompañamos en todo el proceso',
    icon: Sparkles
  }
]

interface FormData {
  user: {
    fullName: string
    email: string
    phone: string
    organization: string
    role: string
  }
  intent: {
    message: string
    subscriptionPlan: {
      name: string
      users: number
      pricePerUser: number
      estimatedMonthlyIncome: number
    }
  }
  compliance: {
    terms: boolean
    privacy: boolean
  }
  captchaToken: string
}

interface ApiResponse {
  status: 'success' | 'error'
  message: string
  data?: {
    mvpId: string
    uuid: string
    timestamp: string
  }
}

/**
 * Contact section component for the landing page
 * Displays a contact form for lead generation
 */
export function Contact() {
  const [formData, setFormData] = useState<FormData>({
    user: {
      fullName: '',
      email: '',
      phone: '',
      organization: '',
      role: ''
    },
    intent: {
      message: '',
      subscriptionPlan: {
        name: 'Pro',
        users: 10,
        pricePerUser: 20,
        estimatedMonthlyIncome: 200
      }
    },
    compliance: {
      terms: false,
      privacy: false
    },
    captchaToken: isDevelopment ? 'development-token' : ''
  })

  const [showSuccess, setShowSuccess] = useState(false)
  const [windowSize, setWindowSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  })

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const contactData = {
      mvpId: 'alineatec-validea-mvp',
      ...formData,
      metadata: {
        timestamp: new Date().toISOString(),
        referrer: document.referrer,
        userAgent: navigator.userAgent,
        language: navigator.language,
        device: {
          type: 'desktop',
          os: navigator.platform,
          browser: navigator.userAgent.split(' ').pop()
        }
      },
      compliance: {
        ...formData.compliance,
        timestamp: new Date().toISOString()
      },
      uuid: uuidv4()
    }

    try {
      const response = await fetch(`${API_URL}/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(contactData)
      })

      const result: ApiResponse = await response.json()

      if (!response.ok || result.status === 'error') {
        throw new Error(result.message || 'Error al enviar el formulario')
      }

      // Mostrar mensaje de éxito y confeti
      setShowSuccess(true)

      // Reset form
      setFormData({
        user: {
          fullName: '',
          email: '',
          phone: '',
          organization: '',
          role: ''
        },
        intent: {
          message: '',
          subscriptionPlan: {
            name: 'Pro',
            users: 10,
            pricePerUser: 20,
            estimatedMonthlyIncome: 200
          }
        },
        compliance: {
          terms: false,
          privacy: false
        },
        captchaToken: isDevelopment ? 'development-token' : ''
      })
    } catch (error) {
      console.error('Error:', error)
      // Aquí podrías mostrar un mensaje de error al usuario
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined

    // Handle nested object updates
    if (name.includes('.')) {
      const [parent, child] = name.split('.')
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof typeof prev] as Record<string, unknown>),
          [child]: type === 'checkbox' ? checked : value
        }
      }))
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }))
    }
  }

  const handleCaptchaVerify = (token: string) => {
    setFormData(prev => ({ ...prev, captchaToken: token }))
  }

  if (showSuccess) {
    return (
      <section className="py-24 sm:py-32 relative">
        <ReactConfetti
          width={windowSize.width}
          height={windowSize.height}
          recycle={true}
          numberOfPieces={200}
          gravity={0.2}
          initialVelocityY={20}
          colors={['#4F46E5', '#818CF8', '#6366F1', '#A5B4FC', '#C7D2FE']}
        />
        <div className="mx-auto max-w-7xl px-6 lg:px-8 relative">
          <div className="mx-auto max-w-2xl text-center">
            <div className="mx-auto h-12 w-12 text-primary-600">
              <CheckCircle2 className="h-12 w-12" />
            </div>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              ¡Gracias por contactarnos!
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              Hemos recibido tu información exitosamente. Nuestro equipo se pondrá en contacto contigo pronto para discutir cómo podemos ayudarte.
            </p>
          </div>
        </div>
      </section>
    )
  }

  return (
    <section id="contact" className="py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-base font-semibold leading-7 text-primary-600">Contacto</h2>
          <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            ¿Listo para validar tu idea?
          </p>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            Dejanos tus datos y te contactamos para ayudarte a empezar
          </p>
        </div>

        <div className="mx-auto mt-16 max-w-2xl">
          <div className="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
            {benefits.map((benefit) => (
              <div
                key={benefit.title}
                className="flex items-start gap-x-4 rounded-2xl border border-gray-200 bg-white p-6 shadow-sm"
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50">
                  <benefit.icon className="h-6 w-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="text-base font-semibold text-gray-900">{benefit.title}</h3>
                  <p className="mt-1 text-sm text-gray-600">{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="mt-10 space-y-6">
            {/* Primera fila: Nombre completo */}
            <div className="sm:col-span-2">
              <label htmlFor="user.fullName" className="block text-sm font-medium leading-6 text-gray-900">
                Nombre completo
                <span className="ml-1 text-gray-400" title="Ingresa tu nombre y apellidos completos">ⓘ</span>
              </label>
              <div className="mt-2.5">
                <input
                  type="text"
                  name="user.fullName"
                  id="user.fullName"
                  required
                  placeholder="Ej: Juan Pérez Rodríguez"
                  value={formData.user.fullName}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Segunda fila: Email y Teléfono */}
            <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
              <div>
                <label htmlFor="user.email" className="block text-sm font-medium leading-6 text-gray-900">
                  Email
                  <span className="ml-1 text-gray-400" title="Correo electrónico empresarial">ⓘ</span>
                </label>
                <div className="mt-2.5">
                  <input
                    type="email"
                    name="user.email"
                    id="user.email"
                    required
                    placeholder="tu@empresa.com"
                    value={formData.user.email}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="user.phone" className="block text-sm font-medium leading-6 text-gray-900">
                  Teléfono
                  <span className="ml-1 text-gray-400" title="Número de teléfono con código de país">ⓘ</span>
                </label>
                <div className="mt-2.5">
                  <input
                    type="tel"
                    name="user.phone"
                    id="user.phone"
                    placeholder="+506 8888-8888"
                    value={formData.user.phone}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            {/* Tercera fila: Empresa y Cargo */}
            <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
              <div>
                <label htmlFor="user.organization" className="block text-sm font-medium leading-6 text-gray-900">
                  Empresa
                  <span className="ml-1 text-gray-400" title="Nombre de tu empresa u organización">ⓘ</span>
                </label>
                <div className="mt-2.5">
                  <input
                    type="text"
                    name="user.organization"
                    id="user.organization"
                    required
                    placeholder="Nombre de tu empresa"
                    value={formData.user.organization}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="user.role" className="block text-sm font-medium leading-6 text-gray-900">
                  Cargo
                  <span className="ml-1 text-gray-400" title="Tu posición o rol en la empresa">ⓘ</span>
                </label>
                <div className="mt-2.5">
                  <input
                    type="text"
                    name="user.role"
                    id="user.role"
                    required
                    placeholder="Ej: Gerente, Director, CTO"
                    value={formData.user.role}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            {/* Cuarta fila: Plan y Cantidad de usuarios */}
            <div className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
              <div>
                <label htmlFor="intent.subscriptionPlan.name" className="block text-sm font-medium leading-6 text-gray-900">
                  Plan de suscripción
                  <span className="ml-1 text-gray-400" title="Selecciona el plan que mejor se ajuste a tus necesidades">ⓘ</span>
                </label>
                <div className="mt-2.5">
                  <select
                    name="intent.subscriptionPlan.name"
                    id="intent.subscriptionPlan.name"
                    required
                    value={formData.intent.subscriptionPlan.name}
                    onChange={handleChange}
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  >
                    <option value="">Seleccionar plan...</option>
                    {subscriptionPlans.map((plan) => (
                      <option key={plan.name} value={plan.name}>
                        {plan.name} (${plan.pricePerUser}/usuario)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="intent.subscriptionPlan.users" className="block text-sm font-medium leading-6 text-gray-900">
                  Cantidad de usuarios
                  <span className="ml-1 text-gray-400" title="Número estimado de usuarios que necesitarás">ⓘ</span>
                </label>
                <div className="mt-2.5">
                  <input
                    type="number"
                    name="intent.subscriptionPlan.users"
                    id="intent.subscriptionPlan.users"
                    min="1"
                    required
                    placeholder="Ej: 10"
                    value={formData.intent.subscriptionPlan.users}
                    onChange={(e) => {
                      const users = parseInt(e.target.value, 10) || 0;
                      const currentPlan = subscriptionPlans.find(p => p.name === formData.intent.subscriptionPlan.name);
                      setFormData(prev => ({
                        ...prev,
                        intent: {
                          ...prev.intent,
                          subscriptionPlan: {
                            ...prev.intent.subscriptionPlan,
                            users,
                            estimatedMonthlyIncome: users * (currentPlan?.pricePerUser || 0)
                          }
                        }
                      }));
                    }}
                    className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            {/* Mensaje */}
            <div className="sm:col-span-2">
              <label htmlFor="intent.message" className="block text-sm font-medium leading-6 text-gray-900">
                Mensaje
                <span className="ml-1 text-gray-400" title="Describe brevemente tus necesidades o preguntas">ⓘ</span>
              </label>
              <div className="mt-2.5">
                <textarea
                  name="intent.message"
                  id="intent.message"
                  rows={4}
                  required
                  placeholder="Cuéntanos sobre tus necesidades específicas y cómo podemos ayudarte..."
                  value={formData.intent.message}
                  onChange={handleChange}
                  className="block w-full rounded-md border-0 px-3.5 py-2 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                />
              </div>
            </div>

            {/* Compliance y hCaptcha */}
            <div className="sm:col-span-2">
              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      type="checkbox"
                      name="compliance.terms"
                      id="compliance.terms"
                      required
                      checked={formData.compliance.terms}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="compliance.terms" className="text-sm text-gray-600">
                      Acepto los{' '}
                      <a href="/terms" className="font-semibold text-primary-600 hover:text-primary-500">
                        términos y condiciones
                      </a>
                    </label>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="flex h-6 items-center">
                    <input
                      type="checkbox"
                      name="compliance.privacy"
                      id="compliance.privacy"
                      required
                      checked={formData.compliance.privacy}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-600"
                    />
                  </div>
                  <div className="ml-3">
                    <label htmlFor="compliance.privacy" className="text-sm text-gray-600">
                      Acepto la{' '}
                      <a href="/privacy" className="font-semibold text-primary-600 hover:text-primary-500">
                        política de privacidad
                      </a>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            {!isDevelopment && (
              <div className="sm:col-span-2">
                <HCaptcha
                  sitekey={HCAPTCHA_SITE_KEY}
                  onVerify={handleCaptchaVerify}
                />
              </div>
            )}

            <div className="mt-10">
              <button
                type="submit"
                disabled={!isDevelopment && !formData.captchaToken}
                className="block w-full rounded-md bg-primary-600 px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-sm hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Enviar
                <ArrowRight className="ml-2 inline-block h-4 w-4" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </section>
  )
} 